import { type WebRuntimeConfig, webRuntimeConfigSchema } from '@bnb/contracts';
import { type KeyValueStorage, safeLocalStorage } from './utils.ts';

/**
 * Runtime config: one web build runs in every stage (build once, promote the same bytes).
 * The stage-specific bits (Cognito pool, live-sync endpoint, stage name) come from
 * /config.json, written by the deploy in AWS and served by the dev API locally
 * (Vite proxies /config.json → http://localhost:8787/api/config.json).
 */
export const CONFIG_URL = '/config.json';

/** Last good config, so the crew page still opens in a barn with no signal. */
export const CONFIG_CACHE_KEY = 'bnb.runtimeConfig';

export class ConfigError extends Error {
  override name = 'ConfigError';
}

/** Validate an already-parsed config.json body. Throws ConfigError with a readable message. */
export function parseConfig(json: unknown): WebRuntimeConfig {
  const result = webRuntimeConfigSchema.safeParse(json);
  if (!result.success) {
    const where = result.error.issues
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ');
    throw new ConfigError(`config.json is not valid — ${where}`);
  }
  return result.data;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface LoadConfigOptions {
  fetchImpl?: FetchLike;
  storage?: KeyValueStorage;
}

/**
 * Fetch + validate /config.json. On a network failure, fall back to the last good copy
 * (the service worker also caches it). An INVALID config never falls back: a broken
 * deploy should be loud, not silently run with old settings.
 */
export async function loadConfig(options: LoadConfigOptions = {}): Promise<WebRuntimeConfig> {
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const storage = options.storage ?? safeLocalStorage;

  let response: Response;
  try {
    response = await fetchImpl(CONFIG_URL, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
  } catch (err) {
    const cached = readCachedConfig(storage);
    if (cached) return cached;
    throw new ConfigError(
      `Could not reach the planner to load its settings (${err instanceof Error ? err.message : 'network error'}). Check your connection and try again.`,
    );
  }

  if (!response.ok) {
    const cached = response.status >= 500 ? readCachedConfig(storage) : null;
    if (cached) return cached;
    throw new ConfigError(`Loading ${CONFIG_URL} failed with HTTP ${response.status}.`);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ConfigError(`${CONFIG_URL} is not JSON. Is the API running? (pnpm dev starts it)`);
  }

  const config = parseConfig(json);
  storage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config));
  return config;
}

function readCachedConfig(storage: KeyValueStorage): WebRuntimeConfig | null {
  const raw = storage.getItem(CONFIG_CACHE_KEY);
  if (!raw) return null;
  try {
    return parseConfig(JSON.parse(raw));
  } catch {
    storage.removeItem(CONFIG_CACHE_KEY);
    return null;
  }
}

/** Short label for the stage badge: LOCAL, STAGING, PR 12, or null for production (no badge). */
export function stageLabel(stage: string): string | null {
  if (stage === 'prod') return null;
  if (stage.startsWith('pr-')) return `PR ${stage.slice(3)}`;
  return stage.toUpperCase();
}
